import * as React from 'react';
import { useCallback } from 'react';
import Button from '../../../components/Buttons/Button/Button';
import { AutomationIds } from '../../../common/enums/AutomationElements.enum';
import { Filter } from '../../../common/classes/Filter/Filter';
import { Column } from '../../../common/interfaces';
import { FilterUI } from './types';
import { useLanguageElements } from '../../../language-elements/withLanguageElementsHOC';

interface FilterFooterProps {
    filter: FilterUI;
    fields: Column[];
    isFilterEmpty: boolean;
    showErrors: boolean;
    setShowErrors: (showErrors: boolean) => void;
    setIsModalOpen: (isOpen: boolean) => void;
    setActiveCondition: (activeCondition: number) => void;
    onApplyFilter: (filter: FilterUI) => Promise<boolean>;
    onDeleteFilter: (filterId: string) => Promise<boolean>;
    onCancelFilter: () => void;
    onModalClose?: () => void;
}

const FilterFooter: React.FC<FilterFooterProps> = ({
    filter,
    fields,
    isFilterEmpty,
    showErrors,
    setShowErrors,
    setIsModalOpen,
    setActiveCondition,
    onApplyFilter,
    onDeleteFilter,
    onCancelFilter,
    onModalClose,
}) => {
    const languageElements = useLanguageElements();
    const handleApplyFilter = useCallback((): void => {
        const newFilter = new Filter(fields, filter, true);
        if (newFilter.isValid) {
            onApplyFilter(newFilter.toDisplayModel())
                .then(() => {
                    setIsModalOpen(false);
                    if (onModalClose) {
                        onModalClose();
                    }
                })
                .catch(() => {
                    setShowErrors(true);
                });
        } else {
            setShowErrors(true);
        }
    }, [fields, filter, onApplyFilter, onModalClose, setIsModalOpen, setShowErrors]);

    const handleDeleteFilter = useCallback((): void => {
        if (filter.id) {
            onDeleteFilter(filter.id).then(() => {
                setActiveCondition(-1);
                setShowErrors(false);
                setIsModalOpen(false);
                if (onModalClose) {
                    onModalClose();
                }
            });
        }
    }, [filter.id, onDeleteFilter, onModalClose, setActiveCondition, setIsModalOpen, setShowErrors]);

    return (
        <div className={'filter-footer'}>
            <Button
                id="filterFooterDeleteBtn"
                tabIndex={0}
                className={'btn btn-secondary'}
                onClick={handleDeleteFilter}
                dataClientId={AutomationIds.FILTER_DELETE_BUTTON}
                disabled={isFilterEmpty || !filter.id}
                text={languageElements.FILTER_FOOTER_DELETE_BUTTON_TEXT}
            />
            <Button
                id="filterFooterApplyBtn"
                tabIndex={1}
                className={'btn btn-primary'}
                onClick={handleApplyFilter}
                dataClientId={AutomationIds.FILTER_APPLY_BUTTON}
                disabled={isFilterEmpty || !filter.isValid || showErrors}
                text={languageElements.FILTER_FOOTER_APPLY_BUTTON_TEXT}
            />
            <Button
                id="filterFooterCancelBtn"
                tabIndex={0}
                className={'btn btn-secondary'}
                onClick={onCancelFilter}
                dataClientId={AutomationIds.FILTER_CANCEL_BUTTON}
                text={languageElements.FILTER_FOOTER_CANCEL_BUTTON_TEXT}
            />
        </div>
    );
};

export default FilterFooter;
